/**
 * Runs at most `limit` tasks at once; the rest wait their turn.
 *
 * For fan-outs where the number of reads follows the data rather than the screen — a month with more
 * sprints or an organization with more products means more requests, all fired at once by default. The
 * API holds a database connection per request, and that database has far fewer connections than a burst
 * has requests, so an unbounded fan-out takes the whole instance down rather than merely being slow.
 *
 * Bounded here rather than at the server: the requests are the client's to pace, and queueing them keeps
 * every read on the same route it already uses.
 */
export const limitConcurrency = (limit: number) => {
  let active = 0;
  const waiting: (() => void)[] = [];

  const release = (): void => {
    active -= 1;
    waiting.shift()?.();
  };

  return async <T>(task: () => Promise<T>): Promise<T> => {
    if (active >= limit) {
      await new Promise<void>(resolve => {
        waiting.push(resolve);
      });
    }
    active += 1;
    try {
      return await task();
    } finally {
      release();
    }
  };
};
